import { useState, useRef } from "react";
import { Anchor, H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";

const InputDocumentsUpload = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const { data, loading, call } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });
  console.log("🚀 ~ InputDocumentsUpload ~ data:", data);

  // Función para subir múltiples archivos
  const uploadMultipleFiles = async (filesArray) => {
    if (!filesArray || filesArray.length === 0) {
      toast.error("No hay archivos para subir");
      return;
    }

    const uploadPromises = filesArray.map(async (file) => {
      try {
        const formData = new FormData();
        formData.append("Name", file.name);
        formData.append("File", file);
        formData.append("originalName", file.name);
        formData.append("Status", "verified");
        formData.append("ProcedureId", field.id);

        const res = await requestAuth("post", "/files", formData);

        // Crear subdata para cada documento
        const subData = {
          label: file.name,
          description: res.data.url,
          description2: new Date().toISOString().split("T")[0], // Fecha actual
          description3: `Tamaño: ${(file.size / 1024).toFixed(2)} KB, Tipo: ${
            file.type || "Desconocido"
          }`,
        };

        await requestAuth(
          "post",
          `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
          subData
        );

        return { success: true, fileName: file.name };
      } catch (error) {
        console.error("Error al subir archivo:", error);
        return { success: false, fileName: file.name, error };
      }
    });

    const results = await Promise.all(uploadPromises);
    const successful = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);

    if (successful.length > 0) {
      toast.success(
        `${successful.length} documento(s) subido(s) correctamente`
      );
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} documento(s) fallaron al subir`);
    }

    call(); // Refrescar la lista
    setSelectedFiles([]); // Limpiar archivos seleccionados
  };

  // Manejar selección de archivos
  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      // Convertir FileList a Array inmediatamente
      const filesArray = Array.from(files);
      setSelectedFiles(filesArray);

      openDialog(
        <DialogConfirmation
          children={
            <div>
              <H3>Subir {filesArray.length} documento(s)</H3>
              <p className="mt-2 text-sm text-gray-600">
                ¿Estás seguro que deseas subir {filesArray.length} documento(s)?
                <br />
                <ul className="mt-2 list-disc pl-5 max-h-40 overflow-y-auto">
                  {filesArray.map((file, index) => (
                    <li key={index} className="text-xs mb-1">
                      <span className="font-medium">{file.name}</span>
                      <span className="text-gray-500 ml-2">
                        ({(file.size / 1024).toFixed(2)} KB)
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-gray-500">
                  Total:{" "}
                  {(
                    filesArray.reduce((acc, file) => acc + file.size, 0) /
                    (1024 * 1024)
                  ).toFixed(2)}{" "}
                  MB
                </div>
              </p>
            </div>
          }
          onClose={() => {
            dialogClose();
            setSelectedFiles([]);
          }}
          method="custom"
          texBtn="Subir documentos"
          handleSuccess={async () => {
            await uploadMultipleFiles(filesArray);
            dialogClose();
          }}
        />
      );
    }
    // Resetear el input de archivos
    event.target.value = "";
  };

  // Función para eliminar un documento
  const handleDelete = (data) => {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estás seguro que quieres eliminar este documento?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={() => {
          call();
          toast.success("Documento eliminado correctamente");
        }}
      />
    );
  };

  // Descargar documento
  const handleDownload = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "documento";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Ver documento
  const handleView = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Función para arrastrar y soltar
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      // Convertir FileList a Array
      const filesArray = Array.from(files);
      setSelectedFiles(filesArray);

      openDialog(
        <DialogConfirmation
          children={
            <div>
              <H3>Subir {filesArray.length} documento(s)</H3>
              <p className="mt-2 text-sm text-gray-600">
                Archivos arrastrados y soltados:
                <br />
                <ul className="mt-2 list-disc pl-5 max-h-40 overflow-y-auto">
                  {filesArray.map((file, index) => (
                    <li key={index} className="text-xs mb-1">
                      <span className="font-medium">{file.name}</span>
                      <span className="text-gray-500 ml-2">
                        ({(file.size / 1024).toFixed(2)} KB)
                      </span>
                    </li>
                  ))}
                </ul>
              </p>
            </div>
          }
          onClose={() => {
            dialogClose();
            setSelectedFiles([]);
          }}
          method="custom"
          texBtn="Subir documentos"
          handleSuccess={async () => {
            await uploadMultipleFiles(filesArray);
            dialogClose();
          }}
        />
      );
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <Label htmlFor={field.name} className={`${field.className} my-0`}>
        {field.labelCustom ? field.labelCustom : field.label}
      </Label>

      <div className="bg-gray-100 rounded-lg p-3 pt-2 mb-4">
        <div
          className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
          />

          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Label className="mt-2 block text-sm font-medium text-gray-700">
              Subir documentos
            </Label>
            <p className="mt-1 text-xs text-gray-500">
              PDF, DOC, XLS, JPG, PNG (Múltiples archivos permitidos)
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Arrastra y suelta archivos aquí o haz clic para seleccionar
            </p>
          </div>

          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <i className="fas fa-cloud-upload-alt mr-2"></i>
            Seleccionar archivos
          </Button>
        </div>

        {/* Mostrar archivos seleccionados temporalmente */}
        {selectedFiles.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
            <p className="text-sm font-medium text-blue-700 mb-2">
              Archivos listos para subir: {selectedFiles.length}
            </p>
            <div className="max-h-20 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div key={index} className="text-xs text-blue-600 truncate">
                  • {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {!loading ? (
        <Circle />
      ) : (
        <div className="w-full">
          {data && data.length > 0 ? (
            <TableComplete
              header={[
                {
                  name: "label",
                  label: "Campo",
                },
                {
                  name: "description",
                  label: "Enlace",
                  type: "custom",
                  builder: (item) => {
                    if (item) {
                      return (
                        <Anchor
                          href={item}
                          className="text-[#1d4ed8] underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Abrir
                        </Anchor>
                      );
                    } else {
                      return "-";
                    }
                  },
                },
                {
                  name: "description2",
                  label: "Fecha de Subida",
                  type: "date",
                },
                {
                  name: "actions",
                  type: "action",
                  label: "Acciones",
                  sticky: true,
                  actions: [
                    {
                      label: "Eliminar",
                      icon: "fas fa-trash",
                      action: handleDelete,
                      color: "text-red-500",
                    },
                  ],
                },
              ]}
              data={data}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <i className="fas fa-folder-open text-4xl mb-2"></i>
              <p>No hay documentos subidos todavía</p>
              <p className="text-sm mt-1">
                Sube tu primer documento arrastrando archivos o haciendo clic en
                "Seleccionar archivos"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InputDocumentsUpload;
